import { InlineIcon } from "@iconify/react";
import {
  Badge,
  Box,
  Button,
  Grow,
  IconButton,
  InputAdornment,
  styled,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  Avatar,
  CircularProgress,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import "./Home.styles.scss";
import NoData from "@/components/No-data/NoData";
import ProfileCard, {
  TProfileCard,
} from "../../Elements/ProfileCard/ProfileCard";
import { apiGetMember, apiGetSponsoredViewers } from "../../api/profile.api";
import { selectSearchQuery } from "@/store/reducers/general_param/general_param.selector";
import { useSelector } from "react-redux";
import useUserRestrictions from "@/hooks/useUserRestriction";
import UserRestrictionDisplayer from "@/components/UserRestrictionDisplayer/UserRestrictionDisplayer";
import ModalResearch from "@/components/Modal-research/ModalResearch";
import ModalUserRestrictionWhenPaid from "@/components/Modal-user-restriction-when-paid/ModalUserRestrictionWhenPaid";
import { calculerAge } from "@/utils/calculerAge";
import { TCountry, TPaginationResult } from "../../types";
import { TMemberProfile } from "@/features/auth/types";
import {
  selectMemberProfile,
  selectMessageNotif,
} from "@/store/reducers/member/member.selector";
import { getMeUpdate } from "../../initialize_param/get_me_update";
import useExchangeRole from "@/hooks/useExchangeRole";
import { store } from "@/store";
import { updateOpenDiscussion } from "@/store/reducers/general_param/general_param.actions";
import { useNavigate, useSearchParams } from "react-router-dom";
import { routePaths } from "@/config";
import { updateToggleSponsoring } from "@/store/reducers/member/member.actions";
import ModalFullProfil from "@/components/Modal-full-profile/ModalFullProfil";
import { Toastify } from "@/utils/toast";
import * as moment from "moment";
import "moment/locale/fr";

export type TProfileList = Array<TProfileCard>;
const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      content: '""',
    },
  },
}));

const CtHome = () => {
  const [isDrawerOpen, setDrawerOpen] = useState(false);

  // Ouvrir/Fermer Drawer
  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };

  const [profileViewers, setProfileViewers] = useState([]);
  const [loadingViewers, setLoadingViewers] = useState(false);

  const getAlpha2CountryCode = (countryName: string): string | null => {
    try {
      const countries: TCountry[] = require("@/assets/json/countries.json");

      // Fonction pour normaliser les chaînes (supprimer les accents, mettre en minuscule)
      const normalizeString = (str: string) =>
        str
          .trim()
          .toLowerCase()
          .replace(/'|'/g, "'")
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, ""); // Remove accents

      const normalizedCountry = normalizeString(countryName);

      const found = countries.find(
        (item) => normalizeString(item.country) === normalizedCountry
      );

      return found ? found.abbreviation : null;
    } catch (error) {
      console.error("Erreur lors de la récupération du code alpha2 :", error);
      return null;
    }
  };

  // Simule un appel API (remplaçable plus tard)
  const apiGetViewers = async () => {
    try {
      setLoadingViewers(true);
      const response = await apiGetSponsoredViewers();
      const data = response?.data || [];

      const parsedViewersList: any[] = data.map((item: any) => ({
        profilId: item._id,
        id: item.memberId,
        age: calculerAge(new Date(item.birthDate)),
        isOnline: item.online,
        username: item.username,
        viewedAt: item.visitedAt,
        origin: getAlpha2CountryCode(item.homeCountry) || "",
        originName: item.homeCountry,
        otherOrigin: getAlpha2CountryCode(item.otherHomeCountry) || "",
        otherOriginName: item.otherHomeCountry,
      }));
      setProfileViewers(parsedViewersList as any);
    } catch (error: any) {
      if (error?.response?.data?.message) {
        Toastify(`Erreur de chargement de mes visiteurs`, "error", 5000);
      }
      // setProfileViewers([...mockProfileViews]);
    } finally {
      setLoadingViewers(false);
    }
  };

  useEffect(() => {
    if (isDrawerOpen) {
      apiGetViewers();
    }
  }, [isDrawerOpen]);

  const theme = useTheme();
  const matches_sm = useMediaQuery(theme.breakpoints.down("sm"));
  const matches_xl = useMediaQuery(theme.breakpoints.down("xl"));
  const nbre_message_notif = useSelector(selectMessageNotif);
  const navigate = useNavigate();
  const { receiver } = useExchangeRole();
  const [searchParams, setSearchParams] = useSearchParams();

  const removeParam = useCallback(() => {
    searchParams.delete("userMode"); // Supprime le paramètre "success"
    setSearchParams(searchParams, { replace: true }); // Met à jour l'URL sans rechargement
  }, [searchParams, setSearchParams]);

  const memberProfileFromStore = useSelector(
    selectMemberProfile
  ) as TMemberProfile;

  const LIMIT_MEMBER = 100;

  const search_query = useSelector(selectSearchQuery);

  const {
    isUserFullAccessAutorized,
    isUserInExchange,
    isUserCloseExchangeInProgress,
    isUserSponsored,
    didUserHasToPay,
  } = useUserRestrictions();

  const [openSearchModal, setOpenSearchModal] = useState(false);
  const handleOpenSearchModal = () => {
    setOpenSearchModal(true);
    removeParam();
  };
  const handleCloseSearchModal = () => setOpenSearchModal(false);

  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const scrollRef = useRef<any>();
  const [paginationResult, setPaginationResult] =
    useState<TPaginationResult | null>(null);

  const [searchedText, setSearchedText] = useState("");

  const [membersList, setMembersList] = useState<TProfileList>([]);

  const getFilteredMember = useCallback(
    (page: number, searchValue: -1 | 0, queryFromSearch: string) => {
      if (isUserInExchange || isUserCloseExchangeInProgress) {
      } else {
        if (isUserFullAccessAutorized) {
          setLoading(true);
          apiGetMember(
            page,
            LIMIT_MEMBER,
            queryFromSearch +
              `${searchValue === -1 ? "" : searchedText === "" ? "" : "&identifier=" + searchedText}`
          )
            .then((response: any) => {
              setLoading(false);
              console.log(response);

              let tempList: TProfileList;
              tempList = response.data.result.map((data: any) => {
                return {
                  _id: data._id,
                  isVerified: data.status === "VALIDATED",
                  username: data.username,
                  type: "",
                  progressValue: Math.round(data.rateOfCompletion),
                  profil_id: data.memberId,
                  isOnline: data?.online,
                  age: calculerAge(new Date(data.birthDate)),
                  town: data.city,
                  country: data.country,
                  origine: data.homeCountry,
                  otherHomeCountry: data.otherHomeCountry,
                  nationalite: data.citizenship,
                  hasMore: false,
                  sendStatut: "",
                  description: data.description,
                  profile_wanted: data.profilewanted,
                  currentConversation: data.currentConversation,
                  date_of_creation: new Date(data.registrationDate),
                  last_connection:
                    data.lastLogin !== null ? new Date(data.lastLogin) : null,
                  isSimpleFavorite: data.isFavorite,
                  isMutualFavorite: false,
                  isVisited: data.isVisited,
                  isVisitor: data.isVisitor,
                  isListeNoire: false,
                  is_banished: false,
                  hasSubscription: data.hasSubscription,
                  gender:
                    memberProfileFromStore.infos.identity.gender === "MALE"
                      ? "FEMALE"
                      : "MALE",
                  isSponsored: data.isSponsored, // <-- waiting for the backend
                  isNewIncomeProfil: data.isNew,
                  hasAlreadyBeenInExchange: data.hasChattedBefore,
                };
              });

              console.log([...tempList]);
              setPaginationResult(response.data.paginationResult);
              setMembersList((prevData) => [...prevData, ...tempList]);
            })
            .catch((error) => {
              console.log("ERROR", error);
              setLoading(false);
              setMembersList([]);
              setPage(1);
              //console.log(error);
              // error?.response?.data?.message &&
              //   Toastify(`${error?.response?.data?.message}`, "error", 5000);
            });
        }
      }
    },
    [
      isUserInExchange,
      isUserCloseExchangeInProgress,
      isUserFullAccessAutorized,
      searchedText,
      memberProfileFromStore.infos.identity.gender,
    ]
  );

  const handleSearchChange = useCallback(
    (e: any) => {
      console.log(e?.target?.value);
      setSearchedText(e?.target?.value);
      setPage(1);

      if (e?.target?.value === "") {
        getFilteredMember(1, -1, search_query);
      }
    },
    [getFilteredMember, search_query]
  );

  const clearClick = useCallback(() => {
    setPage(1);
    setSearchedText("");
    setMembersList([]);
    getFilteredMember(1, -1, search_query);
    removeParam();
  }, [getFilteredMember, search_query, removeParam]);

  const searchClick = useCallback(() => {
    setPage(1);
    setMembersList([]);
    getFilteredMember(1, 0, search_query);
    removeParam();
  }, [getFilteredMember, search_query, removeParam]);

  useEffect(() => {
    getFilteredMember(1, 0, search_query);
    if (scrollRef?.current) {
      scrollRef.current.scrollTop = 0;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isUserFullAccessAutorized,
    isUserCloseExchangeInProgress,
    isUserInExchange,
  ]);

  const handleScroll = useCallback(() => {
    removeParam();
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 5 && !loading) {
        if (paginationResult?.nextPage === null) {
        } else {
          const nextPage = page + 1;
          setPage(nextPage);
          getFilteredMember(nextPage, 0, search_query);
        }
      }
    }
  }, [
    getFilteredMember,
    search_query,
    loading,
    page,
    paginationResult?.nextPage,
    removeParam,
  ]);

  useEffect(() => {
    const container = scrollRef.current;

    container && container.addEventListener("scroll", handleScroll);

    return () => {
      container && container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, loading, page, scrollRef]);

  const updateMember = useCallback(
    (
      id: string,
      type?:
        | "add-favorite"
        | "remove-favorite"
        | "add-blacklist"
        | "remove-blacklist"
        | "visited"
    ) => {
      const findIndex = membersList.findIndex((elt) => elt._id === id);
      if (findIndex !== -1) {
        const updatedMember = {
          ...membersList[findIndex],
        };
        if (type === "add-favorite") {
          updatedMember.isSimpleFavorite = true;
        }
        if (type === "remove-favorite") {
          updatedMember.isSimpleFavorite = false;
        }
        if (type === "add-blacklist") {
          updatedMember.isListeNoire = true;
        }
        if (type === "remove-blacklist") {
          updatedMember.isListeNoire = false;
        }
        if (type === "visited") {
          updatedMember.isVisited = true;
        }

        setMembersList(
          membersList.map((elt) => (elt._id === id ? updatedMember : elt))
        );
      }
    },
    [membersList]
  );

  useEffect(() => {
    getMeUpdate();
  }, []);

  const goTo = (routes: string) => {
    navigate(routes, { replace: true });
  };

  const [selectedId, setSelectedid] = useState<string>("");

  const [openFullProfil, setOpenFullProfil] = React.useState(false);
  const handleOpenFullProfil = (id: string) => {
    setSelectedid(id);
    setOpenFullProfil(true);
  };
  const handleCloseFullProfil = () => setOpenFullProfil(false);

  const boostDuration = useCallback(() => {
    return Math.abs(
      moment
        .default(
          memberProfileFromStore?.account?.subscription?.lastBoost?.endDate
        )
        .diff(moment.default(new Date()), "days")
    );
  }, [memberProfileFromStore?.account?.subscription?.lastBoost?.endDate]);

  return (
    <>
      {isUserFullAccessAutorized ? (
        <>
          <div className="ct-home">
            {!(isUserInExchange || isUserCloseExchangeInProgress) && (
              <>
                {matches_sm && !didUserHasToPay && (
                  <div className="ct-home__btn-top-container">
                    {isUserSponsored ? (
                      <Button
                        color="primary"
                        className="sponsor-button"
                        onClick={() => {
                          toggleDrawer(true);
                        }}
                        startIcon={
                          <InlineIcon icon="material-symbols:table-eye-rounded" />
                        }
                      >
                        Voir qui m'a vu
                      </Button>
                    ) : (
                      <Button
                        color="primary"
                        className="sponsor-button"
                        onClick={() =>
                          goTo(
                            routePaths.profileChildren.myAccountChildren
                              .sponsorAbsolute
                          )
                        }
                        startIcon={
                          <InlineIcon icon="ic:twotone-rocket-launch" />
                        }
                      >
                        Booster mon profil
                      </Button>
                    )}
                  </div>
                )}

                <div className="ct-home__filter-part">
                  <TextField
                    placeholder="pseudo/id"
                    variant="outlined"
                    name="recherche"
                    className="searchInput"
                    type="text"
                    value={searchedText}
                    onChange={handleSearchChange}
                    onFocus={removeParam}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        searchClick();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Grow
                            in={searchedText === ""}
                            mountOnEnter
                            unmountOnExit
                          >
                            <div>
                              <InlineIcon icon="bi:search" />
                            </div>
                          </Grow>
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Grow
                            in={searchedText !== ""}
                            mountOnEnter
                            unmountOnExit
                          >
                            <div>
                              <IconButton
                                className="sm-btn"
                                size="medium"
                                onClick={clearClick}
                              >
                                <InlineIcon
                                  icon="mdi:erase"
                                  color="var(--ui-medium)"
                                />
                              </IconButton>
                            </div>
                          </Grow>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Grow in={searchedText !== ""} mountOnEnter unmountOnExit>
                    <div>
                      <IconButton
                        style={{
                          background: "var(--ui-primary)",
                        }}
                        className="sm-btn"
                        size="medium"
                        color="secondary"
                        onClick={searchClick}
                      >
                        <InlineIcon icon="icon-park-twotone:search" />
                      </IconButton>
                    </div>
                  </Grow>

                  {matches_sm ? (
                    <>
                      <StyledBadge
                        overlap="rectangular"
                        variant="dot"
                        color="warning"
                        badgeContent={search_query !== "" ? 1 : 0}
                      >
                        <IconButton
                          style={{ background: "var(--ui-tertiary)" }}
                          className="sm-btn"
                          size="medium"
                          color="secondary"
                          onClick={handleOpenSearchModal}
                        >
                          <InlineIcon
                            icon="ic:round-filter-alt"
                            color="var(--ui-primary)"
                          />
                        </IconButton>
                      </StyledBadge>
                    </>
                  ) : (
                    <>
                      <StyledBadge
                        overlap="rectangular"
                        variant="dot"
                        color="warning"
                        badgeContent={search_query !== "" ? 1 : 0}
                      >
                        <Button
                          style={{
                            color: "var(--ui-primary)",
                          }}
                          variant="contained"
                          color="tertiary"
                          className="btn"
                          onClick={handleOpenSearchModal}
                          startIcon={
                            <InlineIcon
                              icon="ic:round-filter-alt"
                              style={{ fontSize: "18px" }}
                            />
                          }
                        >
                          Filtrer
                        </Button>
                      </StyledBadge>
                      <>
                        {!didUserHasToPay && (
                          <>
                            {isUserSponsored ? (
                              <Button
                                color="primary"
                                className="sponsor-button"
                                onClick={() => {
                                  toggleDrawer(true);
                                }}
                                startIcon={
                                  <InlineIcon icon="material-symbols:table-eye-rounded" />
                                }
                              >
                                Voir qui m'a vu
                              </Button>
                            ) : (
                              <Button
                                color="primary"
                                className="sponsor-button"
                                onClick={() =>
                                  goTo(
                                    routePaths.profileChildren.myAccountChildren
                                      .sponsorAbsolute
                                  )
                                }
                                startIcon={
                                  <InlineIcon icon="ic:twotone-rocket-launch" />
                                }
                              >
                                Booster mon profil
                              </Button>
                            )}
                          </>
                        )}
                      </>
                    </>
                  )}
                </div>
                <div className="result">
                  <p>
                    {search_query !== "" && (
                      <>
                        <span>
                          &nbsp; Cette liste est filtrée &nbsp;•&nbsp;{" "}
                        </span>
                        <b
                          style={{ cursor: "pointer" }}
                          onClick={handleOpenSearchModal}
                        >
                          {" "}
                          Modifier les critères{" "}
                        </b>
                      </>
                    )}
                  </p>
                </div>
              </>
            )}

            <div ref={scrollRef} className="ct-home__body-part">
              {
                <>
                  {(isUserInExchange ||
                    isUserCloseExchangeInProgress ||
                    membersList.length === 0) &&
                    !loading && (
                      <>
                        <NoData />
                        {isUserInExchange && (
                          <p
                            style={{
                              width: "100%",
                              textAlign: "center",
                              fontSize: "1.2rem",
                            }}
                          >
                            {" "}
                            Vous êtes actuellement en <b>échange</b>
                            {receiver?.username && (
                              <>
                                {" "}
                                avec{" "}
                                <b style={{ color: "var(--ui-primary)" }}>
                                  {receiver?.username}
                                </b>
                              </>
                            )}
                            . <br />
                            Vous ne pouvez pas avoir accès aux autres profils{" "}
                            <br />
                            <br />
                            <Button
                              variant="contained"
                              onClick={() => {
                                store.dispatch(updateOpenDiscussion(true));
                                navigate(
                                  routePaths.profileChildren.exchangeAbsolute,
                                  {
                                    replace: true,
                                  }
                                );
                              }}
                              startIcon={
                                <InlineIcon icon="bx:message-rounded-detail" />
                              }
                              color="primary"
                              style={{
                                width: "fit-content",
                                fontSize: "1rem",
                                padding: "10px 20px",
                              }}
                            >
                              <>Accéder à l'échange</>
                              {nbre_message_notif > 0 && (
                                <>
                                      
                                  <StyledBadge
                                    overlap="circular"
                                    variant="standard"
                                    color="warning"
                                    badgeContent={nbre_message_notif}
                                  />
                                </>
                              )}
                            </Button>
                          </p>
                        )}
                        {isUserCloseExchangeInProgress && (
                          <p
                            style={{
                              width: "100%",
                              textAlign: "center",
                              fontSize: "1.2rem",
                            }}
                          >
                            {" "}
                            Votre clôture d'échange est actuellement en{" "}
                            <b>examination</b> auprès de nos modérateurs. <br />
                            Vous ne pouvez pas avoir accès aux autres profils{" "}
                          </p>
                        )}
                      </>
                    )}
                  {membersList.length > 0 &&
                    !(isUserInExchange || isUserCloseExchangeInProgress) && (
                      <>
                        <div className="profile-card-grid-container">
                          {membersList
                            .filter((x) => x.isListeNoire === false)
                            .map((value, index) => {
                              return (
                                <ProfileCard
                                  key={index}
                                  _id={value._id}
                                  isOnline={value.isOnline}
                                  isVerified={value.isVerified}
                                  hasMore={value.hasMore}
                                  username={value.username}
                                  description={value.description}
                                  profile_wanted={value.profile_wanted}
                                  type={value.type}
                                  age={value.age}
                                  town={value.town}
                                  country={value.country}
                                  origine={value.origine}
                                  otherHomeCountry={value.otherHomeCountry}
                                  profil_id={value.profil_id}
                                  nationalite={value.nationalite}
                                  progressValue={value.progressValue}
                                  sendStatut={value.sendStatut}
                                  currentConversation={
                                    value.currentConversation
                                  }
                                  date_of_creation={value.date_of_creation}
                                  last_connection={value.last_connection}
                                  isSimpleFavorite={value.isSimpleFavorite}
                                  isMutualFavorite={value.isMutualFavorite}
                                  isVisited={value.isVisited}
                                  isVisitor={value.isVisitor}
                                  gender={value.gender}
                                  hasSubscription={value.hasSubscription}
                                  refreshHome={(
                                    type?:
                                      | "add-favorite"
                                      | "remove-favorite"
                                      | "add-blacklist"
                                      | "remove-blacklist"
                                      | "visited"
                                  ) => {
                                    updateMember(value._id || "", type);
                                  }}
                                  is_banished={value.is_banished}
                                  isNewIncomeProfil={value.isNewIncomeProfil}
                                  isSponsored={value.isSponsored}
                                  hasAlreadyBeenInExchange={
                                    value.hasAlreadyBeenInExchange
                                  }
                                />
                              );
                            })}
                        </div>
                      </>
                    )}

                  {loading && (
                    <img
                      src={require("@/assets/images/gif/loadmore.gif")}
                      height="70"
                      width="70"
                      className="load-more-home"
                      alt="load-more"
                    />
                  )}
                </>
              }
            </div>
          </div>
          {/* Drawer latéral */}
          <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={() => {
              toggleDrawer(false);
            }}
            PaperProps={{ className: "drawer-profile-view" }}
          >
            <Box className="drawer-content">
              {/* En-tête professionnelle */}
              <Box className="drawer-header">
                <Typography variant="h6" className="drawer-header-title">
                  <InlineIcon icon="fluent:eye-tracking-24-filled" />
                  &nbsp; Visibilité de votre profil
                </Typography>
                <IconButton
                  className="drawer-close-btn"
                  onClick={() => toggleDrawer(false)}
                  size="small"
                >
                  <InlineIcon icon="mdi:close" />
                </IconButton>
              </Box>

              {/* Liste des visiteurs */}
              <List className="visitors-list">
                {loadingViewers ? (
                  <Box textAlign="center" mt={4}>
                    <CircularProgress size={30} color="primary" />
                    <Typography variant="body2" mt={2}>
                      Chargement des visiteurs...
                    </Typography>
                  </Box>
                ) : profileViewers.length === 0 ? (
                  <Typography variant="body1" align="center" mt={4}>
                    Aucun membre n’a encore consulté votre profil.
                  </Typography>
                ) : (
                  profileViewers.map((viewer: any) => (
                    <ListItem
                      key={viewer.id}
                      className="visitor-item"
                      onClick={() => handleOpenFullProfil(viewer.profilId)}
                    >
                      <Box className="visitor-flag-id">
                        <InlineIcon
                          icon={`flag:${viewer.origin.toLowerCase()}-1x1`}
                          className="visitor-flag"
                        />
                        {viewer.otherOrigin && (
                          <InlineIcon
                            icon={`flag:${viewer.otherOrigin.toLowerCase()}-1x1`}
                            className="visitor-flag-other"
                          />
                        )}
                        <Typography className="visitor-id">
                          ID:{viewer.id}
                        </Typography>
                      </Box>

                      <Box className="visitor-info">
                        <Typography className="visitor-username">
                          {viewer.username} <span>({viewer.age} ans)</span> 
                          {viewer.isOnline ? (
                            <div
                              style={{
                                height: "10px",
                                width: "10px",
                                borderRadius: "50%",
                                background: "var(--ui-success)",
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                height: "10px",
                                width: "10px",
                                borderRadius: "50%",
                                background: "var(--ui-gray)",
                              }}
                            />
                          )}
                        </Typography>
                        <Box display="block" alignItems="center" gap={1}>
                          <Typography className="visitor-date">
                            Origine : <span>{viewer.originName}</span>
                          </Typography>
                          <Typography className="visitor-date">
                            <InlineIcon
                              icon="mdi:recent"
                              style={{ marginRight: "5px" }}
                            />
                            Dernière visite :{" "}
                            <span style={{ fontWeight: "bold" }}>
                              {moment.default(viewer.viewedAt).format("LL") +
                                " " +
                                moment.default(viewer.viewedAt).format("LT")}
                            </span>
                          </Typography>
                        </Box>
                      </Box>
                    </ListItem>
                  ))
                )}
              </List>

              {/* Info jours restants */}
              <Box className="boost-status">
                {boostDuration() === 0 ? (
                  <>
                    <Typography className="boost-status-text">
                      ⏳ C'est votre dernier jour de boost actif
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography className="boost-status-text">
                      ⏳ Il vous reste <strong>{} jour(s)</strong> de boost
                      actif
                    </Typography>
                  </>
                )}
              </Box>

              {/* Footer bouton */}
              <Box className="drawer-footer" sx={{ display: "none" }}>
                {false && (
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    startIcon={<InlineIcon icon="ic:twotone-rocket-launch" />}
                    onClick={() =>
                      goTo(
                        routePaths.profileChildren.myAccountChildren
                          .sponsorAbsolute
                      )
                    }
                  >
                    Prolonger mon boost
                  </Button>
                )}
              </Box>
            </Box>
          </Drawer>
          {openFullProfil && (
            <ModalFullProfil
              openModal={openFullProfil}
              closeModal={handleCloseFullProfil}
              profil_id={selectedId}
              _id={selectedId}
            />
          )}
        </>
      ) : (
        <>
          <Grow in={!isUserFullAccessAutorized} mountOnEnter unmountOnExit>
            <div className="ct-home">
              <UserRestrictionDisplayer />
            </div>
          </Grow>
        </>
      )}
      <ModalResearch
        loadingFromHome={loading}
        open={openSearchModal}
        applyFilter={(query: string) => {
          setPage(1);
          setMembersList([]);
          getFilteredMember(1, -1, query);
          if (matches_xl) {
            handleCloseSearchModal();
          }
        }}
        onClose={handleCloseSearchModal}
        closeClick={handleCloseSearchModal}
      />
      {false && <ModalUserRestrictionWhenPaid />}
    </>
  );
};

export default CtHome;
